/**
 * Person profile (`/people/:id`) — docs/08-frontend-and-ux.md §3.4.
 *
 * Three tabs: the **stored** assertions grouped by kind, with the **derived**
 * panel underneath them (M3 — computed live, never stored, and styled so the
 * difference is legible); the biography; and — for elders and admins only — the
 * history of retired and rejected rows with their audit trail (docs/02 §7). The
 * photos tab (M4) carries the gallery, its lightbox and the portrait picker.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  RETIRE_REASON,
  type Person,
  type PersonDetail,
  type RelatedPerson,
  type RetireReason,
} from '@famtree/shared';
import { personsApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { FuzzyDateText } from '../../ui/FuzzyDateField.js';
import { lifespan, personName, relationDescription, toPersonSummary } from './labels.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';
import { afterWrite } from '../proposals/afterWrite.js';
import { DerivedRelationsPanel } from '../kinship/DerivedRelationsPanel.js';
import { PersonGallery } from '../photos/PersonGallery.js';

type Tab = 'family' | 'photos' | 'biography' | 'history';

export function PersonProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { user } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('family');

  const canSeeHistory = user?.role === 'elder' || user?.role === 'admin';
  const canWrite = user?.role === 'admin' || user?.can_propose;

  const profile = useQuery({
    queryKey: ['person', id],
    queryFn: () => personsApi.get(id),
    enabled: id !== '',
  });

  const retire = useMutation({
    mutationFn: (reason: RetireReason) => personsApi.retire(id, { retire_reason: reason }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['persons'] });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      // Outside bootstrap nothing was retired yet — a question was opened about
      // it, and that is where the proposer belongs (docs/05 §1.9).
      navigate(afterWrite(result.proposal ? [result.proposal] : [], '/people'));
    },
  });

  if (profile.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (profile.isError) return <p className={ui.problem}>{problemMessage(profile.error, t)}</p>;

  const { person, relations, open_proposals: openProposals } = profile.data;
  const span = lifespan(person);

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h1 style={{ marginBlockEnd: 'var(--space-1)' }}>{personName(person)}</h1>
          <p className={ui.muted} style={{ margin: 0 }}>
            {span ? (
              <span className="tabular">{span}</span>
            ) : (
              <FuzzyDateText value={person.birth} />
            )}
            {' · '}
            {t(`enums.living_status.${person.living_status}`)}
            {person.birth_place ? ` · ${person.birth_place}` : ''}
          </p>
          {person.nickname && <p className={ui.muted}>“{person.nickname}”</p>}
        </div>
        <span className={styles.spacer} />
        <span className={person.status === 'pending' ? styles.badgePending : styles.badge}>
          {t(`enums.assertion_status.${person.status}`)}
        </span>
      </div>

      {openProposals.length > 0 && (
        <div className={ui.notice} role="status">
          {t('people.pendingProposals', { count: openProposals.length })}{' '}
          {/* Each one is a question the council is being asked about this person;
              a reader who cannot reach it has been told half of something. */}
          {openProposals.map((proposal, index) => (
            <span key={proposal.id}>
              {index > 0 && ', '}
              <Link to={`/proposals/${proposal.id}`}>
                {t(`enums.proposal_kind.${proposal.kind}`)}
              </Link>
            </span>
          ))}
        </div>
      )}

      {user?.person_id && user.person_id !== person.id && (
        <div className={styles.actions}>
          {/* docs/08 §5.1 — the profile entry point into the finder, pre-filled
              with the reader on one side and this person on the other. */}
          <Link
            className={ui.buttonSecondary}
            to={`/kinship?from=${user.person_id}&to=${person.id}`}
          >
            {t('people.howRelated')}
          </Link>
        </div>
      )}

      {canWrite && (
        <div className={styles.actions}>
          <Link className={ui.buttonSecondary} to={`/people/${person.id}/edit`}>
            {t('people.editDetails')}
          </Link>
          <Link className={ui.buttonSecondary} to={`/relations/new?person=${person.id}`}>
            {t('people.addRelation')}
          </Link>
          <RetireButton onRetire={(reason) => retire.mutate(reason)} pending={retire.isPending} />
        </div>
      )}
      {retire.isError && <p className={ui.problem}>{problemMessage(retire.error, t)}</p>}

      <div className={styles.actions}>
        <Link className={ui.buttonSecondary} to={`/tree/${person.id}`}>
          {t('people.showInTree')}
        </Link>
      </div>

      <div className={styles.tabs} role="tablist">
        {(
          [
            'family',
            'photos',
            'biography',
            ...(canSeeHistory ? (['history'] as const) : []),
          ] as Tab[]
        ).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={tab === name ? styles.tabActive : styles.tab}
            onClick={() => setTab(name)}
          >
            {t(`people.tabs.${name}`)}
          </button>
        ))}
      </div>

      {tab === 'family' && (
        <div>
          <RelationGroup title={t('people.groups.unions')} rows={relations.unions} />
          <RelationGroup title={t('people.groups.parents')} rows={relations.parents} />
          <RelationGroup title={t('people.groups.children')} rows={relations.children} />
          <RelationGroup
            title={t('people.groups.siblings')}
            rows={relations.siblings}
            note={t('people.explicitSiblingsOnly')}
          />
          {canWrite && <KeepGoing person={person} relations={relations} />}

          <DerivedRelationsPanel personId={person.id} />
        </div>
      )}

      {tab === 'photos' && (
        <PersonGallery person={toPersonSummary(person)} canWrite={Boolean(user)} />
      )}

      {tab === 'biography' && (
        <div className={ui.card}>
          {person.bio_note ? (
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{person.bio_note}</p>
          ) : (
            <p className={ui.muted} style={{ margin: 0 }}>
              {t('people.noBiography')}
            </p>
          )}
          {person.source_note && (
            <p className={ui.muted}>{t('people.sourceNote', { note: person.source_note })}</p>
          )}
        </div>
      )}

      {tab === 'history' && canSeeHistory && <HistoryPanel personId={person.id} />}
    </div>
  );
}

/**
 * The "keep going from here" strip — for the person entering a family, not the
 * person reading about one.
 *
 * Entering a household means adding six children to the same two parents, and
 * without these links that is six round trips through a name search for a couple
 * whose profile is already on screen. Each link carries the endpoints in the
 * query string, so the new person's form opens with its connections already
 * placed (docs/08 §3.5 step 2).
 */
function KeepGoing({
  person,
  relations,
}: {
  person: Person;
  relations: PersonDetail['relations'];
}): JSX.Element {
  const { t } = useTranslation();
  const parentIds = relations.parents.map((row) => row.person.id);
  const siblingQuery = parentIds.map((parentId) => `parent=${parentId}`).join('&');

  return (
    <div className={styles.actions}>
      <span className={ui.muted}>{t('people.entryHelpers')}</span>
      <Link className={ui.buttonSecondary} to={`/people/new?parent=${person.id}`}>
        {t('people.addChild')}
      </Link>
      {/* One per union: a child of *this couple* carries both parents, which is
          the common case and the one that costs the most to re-enter by hand. */}
      {relations.unions.map((union) => (
        <Link
          key={union.relation.id}
          className={ui.buttonSecondary}
          to={`/people/new?parent=${person.id}&parent=${union.person.id}`}
        >
          {t('people.addChildOfCouple')} · {personName(union.person)}
        </Link>
      ))}
      {parentIds.length > 0 && (
        <Link className={ui.buttonSecondary} to={`/people/new?${siblingQuery}`}>
          {t('people.addSibling')}
        </Link>
      )}
      <Link className={ui.buttonSecondary} to={`/people/new?child=${person.id}`}>
        {t('people.addParent')}
      </Link>
    </div>
  );
}

function RelationGroup({
  title,
  rows,
  note,
}: {
  title: string;
  rows: RelatedPerson[];
  note?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (rows.length === 0) return null;

  return (
    <section>
      <h2 className={styles.groupTitle}>{title}</h2>
      {note && <p className={ui.muted}>{note}</p>}
      <ul className={styles.list}>
        {rows.map(({ relation, person }) => (
          <li key={relation.id}>
            <Link className={styles.row} to={`/people/${person.id}`}>
              <span className={styles.rowName}>{personName(person)}</span>
              <span className={ui.muted}>{relationDescription(relation, t)}</span>
              <span className={styles.spacer} />
              {relation.status === 'pending' && (
                <span className={styles.badgePending}>{t('enums.assertion_status.pending')}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RetireButton({
  onRetire,
  pending,
}: {
  onRetire: (reason: RetireReason) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<RetireReason>('error');

  if (!open) {
    return (
      <button className={ui.buttonSecondary} type="button" onClick={() => setOpen(true)}>
        {t('people.retire')}
      </button>
    );
  }

  return (
    <div className={styles.subForm} style={{ flexBasis: '100%' }}>
      <p style={{ marginBlockStart: 0 }}>{t('people.retireExplain')}</p>
      <label className={ui.field}>
        <span className={ui.label}>{t('people.retireReason')}</span>
        <select
          className={ui.input}
          value={reason}
          onChange={(event) => setReason(event.target.value as RetireReason)}
        >
          {RETIRE_REASON.map((value) => (
            <option key={value} value={value}>
              {t(`enums.retire_reason.${value}`)}
            </option>
          ))}
        </select>
      </label>
      <div className={styles.inlineRow}>
        <button
          className={ui.button}
          type="button"
          disabled={pending}
          onClick={() => onRetire(reason)}
        >
          {t('people.confirmRetire')}
        </button>
        <button className={ui.buttonSecondary} type="button" onClick={() => setOpen(false)}>
          {t('app.cancel')}
        </button>
      </div>
    </div>
  );
}

function HistoryPanel({ personId }: { personId: string }): JSX.Element {
  const { t } = useTranslation();
  const history = useQuery({
    queryKey: ['person-history', personId],
    queryFn: () => personsApi.history(personId),
  });

  if (history.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (history.isError) return <p className={ui.problem}>{problemMessage(history.error, t)}</p>;

  return (
    <div>
      <h2 className={styles.groupTitle}>{t('people.groups.allAssertions')}</h2>
      <ul className={styles.list}>
        {history.data.relation_history.map(({ relation, person }) => (
          <li key={relation.id} className={styles.row}>
            <span className={styles.rowName}>{personName(person)}</span>
            <span className={ui.muted}>{relationDescription(relation, t)}</span>
            <span className={styles.spacer} />
            <span className={styles.badge}>{t(`enums.assertion_status.${relation.status}`)}</span>
          </li>
        ))}
      </ul>

      <h2 className={styles.groupTitle}>{t('people.groups.audit')}</h2>
      <ul className={styles.list}>
        {history.data.audit.map((entry) => (
          <li key={entry.id} className={styles.row}>
            <span className="tabular">{entry.at.slice(0, 19).replace('T', ' ')}</span>
            <span className={styles.rowName}>{entry.action}</span>
          </li>
        ))}
      </ul>
      {history.data.audit.length === 0 && <p className={ui.muted}>{t('people.noAudit')}</p>}
    </div>
  );
}
